import { DetailedSensor } from '@app/app/sensors/DetailedSensor';
import { Degrees, Kilometers, Milliseconds, SpaceObjectType, ZoomValue } from '@ootk/src/main';
import { Operators, SensorList } from './types';

/**
 * USSF Satellite Control Network (SCN, historically AFSCN) remote tracking
 * stations. SGLS S-band TT&C dishes for cooperative satellite contacts from
 * LEO through GEO/HEO - not surveillance sensors.
 *
 * https://en.wikipedia.org/wiki/Satellite_Control_Network
 */
export const scnSensors = <SensorList>{
  SCNNBS: new DetailedSensor({
    objName: 'SCNNBS',
    shortName: 'BOSS',
    id: 0,
    name: 'New Boston SFS, New Hampshire',
    uiName: 'New Boston (BOSS)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>42.944,
    lon: <Degrees>-71.629,
    alt: <Kilometers>0.18,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000, // LEO through GEO/HEO contacts
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United States',
    operator: Operators.USSF,
  }),
  SCNVTS: new DetailedSensor({
    objName: 'SCNVTS',
    shortName: 'COOK',
    id: 0,
    name: 'Vandenberg Tracking Station, California',
    uiName: 'Vandenberg (COOK)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>34.8232, // Approximate
    lon: <Degrees>-120.5024,
    alt: <Kilometers>0.24,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United States',
    operator: Operators.USSF,
  }),
  SCNHTS: new DetailedSensor({
    objName: 'SCNHTS',
    shortName: 'HULA',
    id: 0,
    name: 'Kaena Point SFS, Hawaii',
    uiName: 'Kaena Point (HULA)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>21.5622,
    lon: <Degrees>-158.2536,
    alt: <Kilometers>0.458,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United States',
    operator: Operators.USSF,
  }),
  SCNGTS: new DetailedSensor({
    objName: 'SCNGTS',
    shortName: 'GUAM',
    id: 0,
    name: 'Guam Tracking Station, Guam',
    uiName: 'Guam (GUAM)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>13.615, // Approximate
    lon: <Degrees>144.8556,
    alt: <Kilometers>0.082,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United States',
    operator: Operators.USSF,
  }),
  SCNDGS: new DetailedSensor({
    objName: 'SCNDGS',
    shortName: 'REEF',
    id: 0,
    name: 'Diego Garcia Tracking Station, British Indian Ocean Territory',
    uiName: 'Diego Garcia (REEF)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>-7.27, // Approximate
    lon: <Degrees>72.37,
    alt: <Kilometers>0.003,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United Kingdom',
    operator: Operators.USSF,
  }),
  SCNTTS: new DetailedSensor({
    objName: 'SCNTTS',
    shortName: 'POGO',
    id: 0,
    name: 'Thule Tracking Station, Pituffik SB, Greenland',
    uiName: 'Thule (POGO)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>76.5157,
    lon: <Degrees>-68.5986,
    alt: <Kilometers>0.076,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'Greenland',
    operator: Operators.USSF,
  }),
  SCNOTS: new DetailedSensor({
    objName: 'SCNOTS',
    shortName: 'LION',
    id: 0,
    name: 'Oakhanger Telemetry and Command Station, United Kingdom',
    uiName: 'Oakhanger (LION)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>51.1145,
    lon: <Degrees>-0.9006,
    alt: <Kilometers>0.1,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United Kingdom',
    operator: Operators.USSF,
  }),
  SCNCTS: new DetailedSensor({
    objName: 'SCNCTS',
    shortName: 'PIKE',
    id: 0,
    name: 'Colorado Tracking Station, Schriever SFB, Colorado',
    uiName: 'Colorado (PIKE)',
    system: 'SCN Remote Tracking Station',
    freqBand: 'SGLS S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>38.8043,
    lon: <Degrees>-104.5289,
    alt: <Kilometers>1.915,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>3,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000,
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://en.wikipedia.org/wiki/Satellite_Control_Network',
    country: 'United States',
    operator: Operators.USSF,
  }),
};
